/**
 * /admin/data-gap-reports — Family-submitted "help us fix this"
 * inbox. Every empty cell on a course page posts here.
 *
 * Design brief (Feb 2026): match the tone of /admin/data-flags but
 * with looser triage (most reports are "here's a URL you missed",
 * not "your writeup is wrong"). Actions: reviewed / actioned /
 * dismissed. Include a mailto reply button when the reporter left an
 * email — one click into the operator's mail client.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, ExternalLink, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import ApplyFixDialog from "@/components/admin/ApplyFixDialog";
import RecentlyEditedStrip from "@/components/admin/RecentlyEditedStrip";

const STATUS_TABS = [
  { key: "new",       label: "New" },
  { key: "reviewed",  label: "Reviewed" },
  { key: "actioned",  label: "Actioned" },
  { key: "dismissed", label: "Dismissed" },
  { key: "all",       label: "All" },
];

const RELATION_LABEL = {
  prospective: "Prospective student",
  student:     "Current student",
  alumni:      "Alumni",
  parent:      "Parent / guardian",
  faculty:     "Faculty",
  admin:       "Admin staff",
  counsellor:  "Counsellor",
  other:       "Other",
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function ReportRow({ report, onStatusChange, registry, onApplyFix }) {
  const [busy, setBusy] = useState(false);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(
        `/admin/data-gap-reports/${report.id}/status?status=${status}`,
      );
      toast.success(`Marked ${status}`);
      onStatusChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  const mailto = useMemo(() => {
    if (!report.contact_email) return null;
    const subject = encodeURIComponent(
      `Re: your WUA feedback on ${report.field || "a course cell"}`,
    );
    const body = encodeURIComponent(
      `Hi,\n\nThanks for flagging "${report.field || "a data cell"}"` +
      (report.degree_title ? ` on ${report.degree_title}` : "") +
      (report.university_name ? ` (${report.university_name})` : "") +
      `.\n\nYou wrote:\n> ${report.hint || "(no note)"}\n\n` +
      `Just following up — `
    );
    return `mailto:${report.contact_email}?subject=${subject}&body=${body}`;
  }, [report]);

  const canApplyFix = useMemo(() => {
    if (!registry || !report.section || !report.field) return false;
    return registry.some(
      (f) => f.section === report.section && f.label === report.field,
    );
  }, [registry, report]);

  return (
    <li
      className="p-4 border-b border-stone-200 last:border-b-0 space-y-2"
      data-testid={`gap-report-row-${report.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-underground-sc tracking-widest text-stone-500">
              {report.section || "unknown-section"}
            </span>
            {report.field && (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-800 font-medium">
                {report.field}
              </span>
            )}
            {report.reporter_relation && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">
                {RELATION_LABEL[report.reporter_relation] || report.reporter_relation}
              </span>
            )}
            {report.contact_email && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800">
                left email
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-stone-900 truncate">
            <span className="font-medium">
              {report.degree_title || report.degree_id || "Unknown course"}
            </span>
            {report.university_name && (
              <span className="text-stone-500"> · {report.university_name}</span>
            )}
          </div>
        </div>
        <span className="text-[10px] text-stone-400 font-mono whitespace-nowrap mt-0.5">
          {fmt(report.created_at)}
        </span>
      </div>

      {report.hint ? (
        <blockquote className="text-sm text-stone-700 bg-stone-50 border-l-2 border-purple-300 pl-3 py-1.5 whitespace-pre-wrap break-words">
          {report.hint}
        </blockquote>
      ) : (
        <p className="text-[12px] italic text-stone-400">
          (No hint provided — flag only.)
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {report.degree_id && (
          <a
            href={`/degrees/${report.degree_id}`}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-purple-700 hover:underline inline-flex items-center gap-1"
            data-testid={`gap-report-degree-link-${report.id}`}
          >
            <ExternalLink className="h-3 w-3" /> Open course page
          </a>
        )}
        {mailto && (
          <a
            href={mailto}
            className="text-[11px] text-purple-700 hover:underline inline-flex items-center gap-1"
            data-testid={`gap-report-mailto-${report.id}`}
          >
            <Mail className="h-3 w-3" /> Reply to {report.contact_email}
          </a>
        )}
        <div className="ml-auto flex gap-1.5">
          {canApplyFix && report.status !== "actioned" && (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => onApplyFix(report)}
              data-testid={`gap-report-apply-fix-${report.id}`}
              className="h-7 text-[11px] bg-purple-800 hover:bg-purple-900 text-white gap-1"
            >
              <Wrench className="h-3 w-3" /> Apply fix
            </Button>
          )}
          {report.status !== "actioned" && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setStatus("actioned")}
              data-testid={`gap-report-actioned-${report.id}`}
              className="h-7 text-[11px]"
            >
              Actioned
            </Button>
          )}
          {report.status !== "reviewed" && report.status === "new" && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setStatus("reviewed")}
              data-testid={`gap-report-reviewed-${report.id}`}
              className="h-7 text-[11px]"
            >
              Reviewed
            </Button>
          )}
          {report.status !== "dismissed" && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => setStatus("dismissed")}
              data-testid={`gap-report-dismissed-${report.id}`}
              className="h-7 text-[11px] text-stone-500"
            >
              Dismiss
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

export default function AdminDataGapReports() {
  const { user, loading: authLoading } = useAuth();
  const [statusFilter, setStatusFilter] = useState("new");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [applyingFix, setApplyingFix] = useState(null);
  const [stripRefreshKey, setStripRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        limit: "200",
      });
      if (sectionFilter !== "all") params.set("section", sectionFilter);
      const [listRes, sumRes] = await Promise.all([
        api.get(`/admin/data-gap-reports?${params.toString()}`),
        api.get(`/admin/data-gap-reports/summary`),
      ]);
      setItems(listRes.data.items || []);
      setSummary(sumRes.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sectionFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Hydrate the field registry once — it drives which reports show
    // an "Apply fix" button and what input type the dialog renders.
    (async () => {
      try {
        const { data } = await api.get("/admin/field-edits/registry");
        setRegistry(data.fields || []);
      } catch {
        setRegistry([]);
      }
    })();
  }, []);

  const sections = useMemo(() => {
    const s = new Set(items.map((r) => r.section).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  if (authLoading) {
    return <div className="p-6 text-stone-400 text-sm"><Loader2 className="inline h-3 w-3 animate-spin mr-1" /> Loading…</div>;
  }
  if (!user) {
    return (
      <div className="p-6 text-stone-600">Sign in as admin to view this page.</div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-stone-900"
          data-testid="gap-reports-back"
        >
          <ArrowLeft className="h-3 w-3" /> Admin home
        </Link>
        <h1 className="font-editorial text-4xl sm:text-5xl text-stone-900 mt-4">
          Data gap reports
        </h1>
        <p className="mt-2 text-stone-600 text-sm max-w-2xl">
          Families flag empty cells on course pages via the tiny "help
          us improve" link. Their notes land here.
        </p>

        <RecentlyEditedStrip refreshKey={stripRefreshKey} />

        {/* Counts strip */}
        {summary && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="gap-reports-counts">
            {STATUS_TABS.filter((t) => t.key !== "all").map((t) => (
              <div
                key={t.key}
                className={`border rounded-lg px-3 py-2 text-xs ${
                  statusFilter === t.key
                    ? "border-purple-400 bg-purple-50 text-purple-900"
                    : "border-stone-200 bg-white text-stone-700"
                }`}
              >
                <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
                  {t.label}
                </div>
                <div className="text-xl font-mono font-semibold">
                  {summary.counts?.[t.key] ?? 0}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setStatusFilter(t.key)}
                data-testid={`gap-reports-tab-${t.key}`}
                className={`text-[11px] font-underground-sc tracking-wider px-2.5 py-1 rounded ${
                  statusFilter === t.key
                    ? "bg-stone-900 text-white"
                    : "bg-white border border-stone-200 text-stone-700 hover:bg-stone-100"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {sections.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[11px] text-stone-500">Section:</span>
              <Select value={sectionFilter} onValueChange={setSectionFilter}>
                <SelectTrigger
                  className="h-8 text-xs w-44"
                  data-testid="gap-reports-section-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sections</SelectItem>
                  {sections.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* List */}
        <div className="mt-4 border border-stone-200 rounded-xl bg-white overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-stone-500">
              <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Loading…
            </div>
          ) : err ? (
            <div className="p-6 text-sm text-rose-700">Error: {err}</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-stone-500" data-testid="gap-reports-empty">
              No reports in this bucket.
            </div>
          ) : (
            <ul>
              {items.map((r) => (
                <ReportRow
                  key={r.id}
                  report={r}
                  onStatusChange={load}
                  registry={registry}
                  onApplyFix={setApplyingFix}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      <ApplyFixDialog
        open={!!applyingFix}
        onOpenChange={(v) => { if (!v) setApplyingFix(null); }}
        report={applyingFix}
        registry={registry}
        onSuccess={() => {
          setApplyingFix(null);
          setStripRefreshKey((k) => k + 1);
          load();
        }}
      />
    </div>
  );
}
